import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export interface TransactionSubCategory {
  slug: string;
  label: string;
}

export interface TransactionCategory {
  slug: string;
  label: string;
  sub_categories: TransactionSubCategory[];
}

export interface TransactionCategoryTree {
  account_type: "consumer" | "business";
  categories: TransactionCategory[];
}

export interface UseTransactionCategoriesResult {
  categories: TransactionCategory[];
  labelForSlug: (slug: string) => string;
  isLoading: boolean;
  error: string | null;
}

export interface UseTransactionCategoriesOptions {
  /** Fetch this tree instead of the caller's own. Omit for the caller's own type. */
  accountType?: "consumer" | "business";
  /** False skips the request entirely, leaving an empty tree. Defaults to true. */
  enabled?: boolean;
}

export const TRANSACTION_CATEGORIES_QUERY_KEY = "transactionCategories";

// The taxonomy only changes on deploy, so one fetch serves the whole session.
const SESSION = Infinity;

const EMPTY: TransactionCategory[] = [];

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "An unexpected error occurred";
};

export const useTransactionCategories = ({
  accountType,
  enabled = true,
}: UseTransactionCategoriesOptions = {}): UseTransactionCategoriesResult => {
  const { data, isLoading, error } = useQuery<TransactionCategoryTree>({
    queryKey: [TRANSACTION_CATEGORIES_QUERY_KEY, accountType ?? null],
    queryFn: () =>
      ApiHttp.fetch<TransactionCategoryTree>(
        accountType
          ? `transaction_categories?account_type=${accountType}`
          : "transaction_categories"
      ),
    enabled,
    staleTime: SESSION,
    gcTime: SESSION,
    retry: false,
  });

  const categories = data?.categories ?? EMPTY;

  // Both levels go in one map. No slug is both a category and a sub-category within a tree, and a
  // response only ever carries one tree, so the lookup cannot be ambiguous.
  const labelBySlug = useMemo(() => {
    const labels = new Map<string, string>();
    categories.forEach((category) => {
      labels.set(category.slug, category.label);
      category.sub_categories.forEach((subCategory) => {
        labels.set(subCategory.slug, subCategory.label);
      });
    });
    return labels;
  }, [categories]);

  const labelForSlug = useCallback(
    (slug: string) => labelBySlug.get(slug) ?? slug,
    [labelBySlug]
  );

  return {
    categories,
    labelForSlug,
    isLoading,
    error: error ? extractErrorMessage(error) : null,
  };
};
